import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { registerUser } from '../../../../services/authApi';
import * as yup from 'yup';
import './styles.css';
import back from '../../../../static/img/back.svg';
import tickVerde from '../../../../static/img/tickVerde.svg';
import CuadroTexto from '../../../../static/assets/cuadroTexto/cuadroTexto';
import LoadingScreen from '../../../../static/assets/loadingScreen/loadingScreen';

const schema = yup.object().shape({
  email: yup
    .string()
    .email('Ingresa un email válido')
    .required('El email es requerido'),
  password: yup
    .string()
    .required('La contraseña es requerida')
    .min(8, 'La contraseña debe tener al menos 8 caracteres')
    .matches(/[A-Z]/, 'La contraseña debe tener al menos una letra mayúscula')
    .matches(/[a-z]/, 'La contraseña debe tener al menos una letra minúscula')
    .matches(/[0-9]/, 'La contraseña debe tener al menos un número'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Las contraseñas no coinciden')
    .required('La confirmación de la contraseña es requerida'),
});

const DeportistaRegistro = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    yup
      .reach(schema, 'email')
      .validate(value)
      .then(() => setErrors((prev) => ({ ...prev, email: '' })))
      .catch((err) => setErrors((prev) => ({ ...prev, email: err.message })));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    yup
      .reach(schema, 'password')
      .validate(value)
      .then(() => setErrors((prev) => ({ ...prev, password: '' })))
      .catch((err) =>
        setErrors((prev) => ({ ...prev, password: err.message }))
      );
    if (confirmPassword && value !== confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: 'Las contraseñas no coinciden',
      }));
    } else {
      setErrors((prev) => ({ ...prev, confirmPassword: '' }));
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (password !== value) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: 'Las contraseñas no coinciden',
      }));
    } else {
      setErrors((prev) => ({ ...prev, confirmPassword: '' }));
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await schema.validate(
        { email, password, confirmPassword },
        { abortEarly: false }
      );
      const userData = { name: email, email, password };
      await registerUser(userData);
      setErrorMessage(`Registro exitoso para: ${email}. Te enviamos un correo de confirmación para activar tu cuenta.`);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (validationErrors) {
      if (validationErrors.name === 'ValidationError') {
        const formattedErrors = {};
        validationErrors.inner.forEach((error) => {
          formattedErrors[error.path] = error.message;
        });
        setErrors(formattedErrors);
      } else if (validationErrors.response) {
        setErrorMessage(
          validationErrors.response.data.error.email ||
            'Hubo un error en el registro.'
        );
      } else {
        setErrorMessage('Hubo un error en el registro. Inténtalo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => navigate('/login');

  useEffect(() => {
    document.body.classList.add('no-footer-padding');
    return () => document.body.classList.remove('no-footer-padding');
  }, []);

  const passwordRequirements = [
    { regex: /.{8,}/, label: 'Debe tener al menos 8 caracteres' },
    { regex: /[A-Z]/, label: 'Debe contener una letra mayúscula' },
    { regex: /[a-z]/, label: 'Debe contener una letra minúscula' },
    { regex: /[0-9]/, label: 'Debe contener un número' },
  ];

  const checkRequirement = (req) => req.regex.test(password);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div translate="no" className="registro-page notranslate">
      {errorMessage && (
        <CuadroTexto
          message={errorMessage}
          onAccept={() => setErrorMessage('')}
          showCancel={false}
        />
      )}

      <div className='registro-header'>
        <button className="back" onClick={handleBack} translate="no">
          <img src={back} alt="Regresar" />
        </button>
        <div className="registro" translate="no">
          <h1 className="registro-title-h">Registrarse</h1>
        </div>       
      </div>
      <form className="registro-form" onSubmit={handleSubmit} translate="no">
        <div className="form-group" translate="no">
          <label htmlFor="email" translate="no">
            Ingresa tu email
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="Email"
            required
            style={{ borderColor: errors.email ? 'red' : '' }}
            translate="no"
          />
          {errors.email && (
            <small className="error-message" translate="no">
              {errors.email}
            </small>
          )}
        </div>

        <div className="form-group" translate="no">
          <label htmlFor="password" translate="no">
            Contraseña
          </label>
          <div className="password-input-container">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="Contraseña"
              required
              style={{ borderColor: errors.password ? 'red' : '' }}
              translate="no"
            />
            <button
              type="button"
              className="toggle-password"
              onClick={togglePasswordVisibility}
              translate="no"
            >
              {showPassword ? <AiOutlineEye /> : <AiOutlineEyeInvisible />}
            </button>
          </div>
          <ul className="password-hint" translate="no">
            {passwordRequirements.map((req, index) => (
              <li
                key={index}
                className={checkRequirement(req) ? 'valid' : 'invalid'}
                translate="no"
              >
                {checkRequirement(req) ? (
                  <img
                    src={tickVerde}
                    alt="✔"
                    className="tick-icon"
                    translate="no"
                  />
                ) : (
                  '❌'
                )}{' '}
                {req.label}
              </li>
            ))}
          </ul>
        </div>

        <div className="form-group" translate="no">
          <label htmlFor="confirm-password" translate="no">
            Confirmar Contraseña
          </label>
          <div className="password-input-container">
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirm-password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              placeholder="Confirmar Contraseña"
              required
              style={{ borderColor: errors.confirmPassword ? 'red' : '' }}
              translate="no"
            />
          </div>
          {errors.confirmPassword && (
            <small className="error-message" translate="no">
              {errors.confirmPassword}
            </small>
          )}
        </div>

        <button
          type="submit"
          className="submit-button"
          disabled={
            !email.trim() ||
            !password ||
            !confirmPassword ||
            errors.email ||
            errors.password ||
            errors.confirmPassword
          }
          translate="no"
        >
          Continuar
        </button>
      </form>
    </div>
  );
};

export default DeportistaRegistro;
